"use client"

import { useEffect, useMemo, useRef } from "react"
import { useFieldArray, useForm } from "react-hook-form"
import { useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import { useAuth } from "@/hooks/useAuth"
import { useResolvedPlacementJobCandidate } from "@/hooks/use-resolved-placement-job-candidate"
import { usePlacementContextSelection } from "@/hooks/use-placement-context-selection"
import {
  describePlacementFailure,
  feeValue,
  validatePlacement,
  type SplitDraft,
} from "@/lib/placement-form"
import type { PlacementRequestFeeType as FeeType } from "openapi/api/model/placementRequestFeeType"
import type { PlacementRequestPlacementType as PlacementType } from "openapi/api/model/placementRequestPlacementType"
import {
  getGetCandidatesCandidateIdJobsQueryKey,
  getGetOrgObsRevenueQueryKey,
  getGetPlacementsQueryKey,
  usePostPlacements,
} from "openapi/api/endpoints"

/**
 * Everything the placement dialog needs to know, so the dialog itself only has
 * to draw canvas 15a: which job and candidate it is writing against, the draft,
 * the money derived from the draft, and the save.
 */

export type PlacementFormData = {
  placementType: PlacementType
  salary: string
  salaryCurrency: string
  fee: string
  feeType: FeeType
  startDate: Date | undefined
  endDate: Date | undefined
  createdDate: Date
  notes: string
  splits: SplitDraft[]
}

export type PlacementBookingArgs = {
  isOpen: boolean
  onClose: () => void
  jobId?: string
  candidateId?: string
  jobCandidateId?: string
  candidateName?: string
  jobTitle?: string
  companyName?: string
  /** Seeds the start date — usually when the job or the pipeline row opened. */
  creationDate?: string
}

function defaults(creationDate?: string): PlacementFormData {
  return {
    placementType: "Full Time",
    salary: "",
    salaryCurrency: "$",
    fee: "",
    feeType: "percentage",
    startDate: creationDate ? new Date(creationDate) : new Date(),
    endDate: undefined,
    createdDate: new Date(),
    notes: "",
    splits: [{ owner: "Me", ownerType: "recruiter", percentage: "100" }],
  }
}

export function usePlacementBooking({
  isOpen,
  onClose,
  jobId,
  candidateId,
  jobCandidateId,
  candidateName,
  jobTitle,
  companyName,
  creationDate,
}: PlacementBookingArgs) {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const wasOpen = useRef(isOpen)

  /*
    Canvas 15a: "the drop already said who and where, so the dialog states it
    and never asks again — no context selector, no job picker." That only holds
    when a drop opened it; the placements page opens the same dialog cold, and
    there the selector is the only way to say who is being placed.
  */
  const hasFixedContext = Boolean(jobId && candidateId)
  const contextSelector = usePlacementContextSelection({
    isOpen: isOpen && !hasFixedContext,
  })

  const effective = {
    jobId: jobId || contextSelector.selection.jobId,
    candidateId: candidateId || contextSelector.selection.candidateId,
    jobCandidateId: jobCandidateId || contextSelector.selection.jobCandidateId,
    jobTitle: jobTitle || contextSelector.selection.jobTitle,
    companyName: companyName || contextSelector.selection.companyName,
    candidateName: candidateName || contextSelector.selection.candidateName,
  }

  const {
    jobCandidate,
    jobCandidateError,
    resolvedJobCandidateId,
    resolvedCandidateName,
    isResolvingJobCandidate,
    hasJobCandidateResolutionError,
  } = useResolvedPlacementJobCandidate({
    enabled: isOpen && hasFixedContext,
    jobId: effective.jobId,
    candidateId: effective.candidateId,
    jobCandidateId: effective.jobCandidateId,
    candidateName: effective.candidateName,
  })

  const targetJobCandidateId = effective.jobCandidateId || resolvedJobCandidateId
  const hasContext = Boolean(
    effective.jobId && effective.candidateId && targetJobCandidateId,
  )

  const { mutateAsync: createPlacement, isPending: isSaving } = usePostPlacements()

  const form = useForm<PlacementFormData>({ defaultValues: defaults(creationDate) })
  const { control, register, handleSubmit, watch, setValue, reset } = form

  // Reopening starts a fresh placement; staying open keeps what was typed.
  useEffect(() => {
    if (isOpen && !wasOpen.current) reset(defaults(creationDate))
    wasOpen.current = isOpen
  }, [creationDate, isOpen, reset])

  const splitFields = useFieldArray({ control, name: "splits" })

  const draft = watch()
  const fee = feeValue({
    fee: draft.fee ?? "",
    feeType: draft.feeType,
    salary: draft.salary ?? "",
  })

  /*
    15a shows money per split, not only percentage — "splits show money, not
    just percentage" — so each row carries what that share is actually worth.
  */
  const splitValues = useMemo(
    () =>
      (draft.splits ?? []).map((split) => {
        const percentage = Number(split.percentage)
        if (fee === undefined || !Number.isFinite(percentage)) return undefined
        return (fee * percentage) / 100
      }),
    [draft.splits, fee],
  )

  const allocated = (draft.splits ?? []).reduce(
    (sum, split) => sum + (Number(split.percentage) || 0),
    0,
  )

  /*
    The pipeline row's own age, which is what "63 days in pipeline" counts —
    not the job's, which is older and would overstate every candidate.
  */
  const openedAt = jobCandidate?.inserted_at
  const daysInPipeline = openedAt
    ? Math.max(0, Math.floor((Date.now() - new Date(openedAt).getTime()) / 86400000))
    : undefined

  const submit = handleSubmit(async (data) => {
    const result = validatePlacement({
      jobCandidateId: targetJobCandidateId,
      placementType: data.placementType,
      feeType: data.feeType,
      splits: data.splits,
    })

    if (!result.ok) {
      toast.error(result.problem.title, { description: result.problem.description })
      return
    }

    try {
      await createPlacement({
        data: {
          job_candidate_id: targetJobCandidateId,
          placement_type: data.placementType,
          salary: Number(data.salary || "0"),
          fee_amount: Number(data.fee || "0"),
          fee_type: data.feeType,
          start_date: data.startDate?.toISOString(),
          end_date: data.endDate?.toISOString(),
          notes: data.notes ?? "",
          splits: result.splits.map((split) => ({
            owner_name: split.owner,
            owner_type: split.ownerType,
            percentage: split.percentage,
            ...(split.owner === "Me" && user?.id ? { owner_id: user.id } : {}),
          })),
        },
      })

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: getGetPlacementsQueryKey(), exact: false }),
        queryClient.invalidateQueries({
          queryKey: getGetCandidatesCandidateIdJobsQueryKey(effective.candidateId),
          exact: false,
        }),
        queryClient.invalidateQueries({ queryKey: getGetOrgObsRevenueQueryKey(), exact: false }),
      ])

      toast.success("Placement booked", {
        description: `${
          resolvedCandidateName || effective.candidateName || "The candidate"
        } is placed.`,
      })
      onClose()
    } catch (error) {
      toast.error("Could not book the placement", {
        description: describePlacementFailure(error),
      })
    }
  })

  return {
    register,
    setValue,
    draft,
    splitFields,

    hasFixedContext,
    contextSelector,
    candidateName: effective.candidateName || resolvedCandidateName,
    jobTitle: effective.jobTitle,
    companyName: effective.companyName,

    fee,
    splitValues,
    allocated,
    allocationBalanced: Math.abs(allocated - 100) <= 0.01,
    openedAt,
    daysInPipeline,

    isResolving: isResolvingJobCandidate,
    hasResolutionError: hasJobCandidateResolutionError,
    resolutionErrored: Boolean(jobCandidateError),
    canSave: hasContext && !isResolvingJobCandidate && !hasJobCandidateResolutionError,
    isSaving,
    submit,
  }
}
